import { create } from 'zustand'
import { api, ApiError } from '@/api/client'
import type { User, Clinic, EmailVerificationStatus } from '@/api/types'
import { apiUrl } from '@/lib/api-url'
import { setRealm } from '@/lib/realm'
import { applyColorTheme } from '@/lib/color-theme'
import { setSentryUser } from '@/sentry'

/**
 * CLA-1015 — /auth/me bootstrap retry state.
 *
 * Only a definitive 401/403 may clear the session. A 429 (rate limit), 5xx, or
 * network error says nothing about the cookie's validity — the server just
 * couldn't answer right now — so `init` keeps the current auth state and
 * retries in the background: an established session stays assumed-valid, and a
 * fresh boot keeps its loading spinner instead of rendering "signed out".
 */
const INIT_RETRY_BASE_MS = 2_000
const INIT_RETRY_MAX_MS = 30_000
let initRetryTimer: ReturnType<typeof setTimeout> | null = null
let initRetryAttempt = 0

/** Cancel any scheduled background /auth/me retry (logout + tests). */
export function cancelInitRetry(): void {
  if (initRetryTimer) {
    clearTimeout(initRetryTimer)
    initRetryTimer = null
  }
  initRetryAttempt = 0
}

interface AuthState {
  user: User | null
  clinics: Clinic[]
  activeClinicId: number | null
  activeNetworkId: number | null
  /**
   * Server's answer to "is this a brand-new self-serve owner who still needs
   * to create their business?" — true only when the user holds no clinic, org
   * OR network membership (CLA-1183).
   *
   * `null` means a backend that predates the field; callers fall back to
   * `clinics.length === 0`, the old behaviour. Do NOT use an empty clinics
   * list as a substitute: a network- or org-only member has no clinic rows
   * and is not a new owner, and gating on the length walked a franchisor CEO
   * through provisioning himself a clinic in production.
   */
  needsOwnerOnboarding: boolean | null
  /**
   * Whether this account has proven its email address (CLA-1018). A
   * self-serve signup is signed in *before* clicking the link, so the shell
   * needs to know whether to show the verify banner (and, past the 7-day
   * grace, the gate). `null` means a backend that predates the field — no
   * banner, nothing to prove; those accounts were verified by construction.
   */
  emailVerification: EmailVerificationStatus | null
  isLoading: boolean
  /**
   * True while `/auth/me` is failing transiently and a retry is scheduled
   * (CLA-1170). Distinct from `isLoading`, which cannot tell a first paint
   * apart from a wait: the boot spinner alone looks identical to a hung page,
   * and a rate-limited wait can run to the end of the server's window.
   */
  isRetrying: boolean
  isAuthenticated: boolean
  /**
   * Holds ANY named admin grant — `admin:settings`, `admin:users`, … — which
   * every clinic_admin and org_admin does, so every clinic owner is `isAdmin`
   * (CLA-993). It means "runs a business on Claro", NOT "runs Claro". Use
   * `isPlatformAdmin` for the platform surfaces; the server gates those on the
   * literal `admin:*`, and gating them on this flag put the Platform nav and
   * a 403-ing feedback poll in front of every clinic owner (CLA-1249).
   */
  isAdmin: boolean
  /**
   * Holds `*` or the literal `admin:*` — mirrors the server's `:require_admin`.
   * Derived from `permissions` at call time (like `canManageClinic`) so state
   * set directly, as tests do, cannot disagree with it.
   */
  isPlatformAdmin: () => boolean
  /**
   * Platform STAFF: superadmin or support. Mirrors the server's
   * `Claro.NoteProfiles.platform_staff?/1`, which reads `users.platform_roles`
   * (returned on `/auth/me` as `user.platform_roles`), not the permission set.
   * Support holds no `admin:*`, so this is wider than `isPlatformAdmin`, and
   * it opens only what the server gates on staff (the Help library), never
   * the rest of Manage → Platform.
   */
  isPlatformStaff: () => boolean
  demoMode: boolean
  /**
   * Whether this deployment can place a video call at all. Absent from an
   * older backend means "assume yes" — the call endpoints still answer 503
   * `telehealth_unavailable` on their own; this only lets the shell say so
   * before the button is pressed (CLA-1250).
   */
  telehealthConfigured: boolean
  permissions: string[]

  /** Check if the user has a specific permission (supports wildcards) */
  hasPermission: (permission: string) => boolean

  /** Whether the user can manage the active clinic's plan/settings (CLA-906) */
  canManageClinic: () => boolean

  /** The user's role at the active clinic, or null outside any clinic membership. */
  activeClinicRole: () => string | null

  /**
   * A clinic_viewer at the active clinic: reads notes and patients, reports
   * incidents, and cannot record, write notes or create patients. Drives what
   * the shell offers, not what the API allows — the server still decides.
   */
  isReadOnly: () => boolean

  /** Load user context from the API (called on app mount) */
  init: () => Promise<void>

  /**
   * Re-read `GET /auth/email-verification` and store the answer. Cheaper
   * than `init()` after a resend or a code entry; the banner calls it to
   * close itself from the server's answer rather than assuming the 200.
   */
  refreshEmailVerification: () => Promise<EmailVerificationStatus | null>

  /** Switch the active clinic */
  switchClinic: (clinicId: number) => Promise<void>

  /** Sign out — clears session and redirects to login */
  logout: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  clinics: [],
  activeClinicId: null,
  activeNetworkId: null,
  needsOwnerOnboarding: null,
  emailVerification: null,
  isLoading: true,
  isRetrying: false,
  isAuthenticated: false,
  isAdmin: false,
  demoMode: false,
  telehealthConfigured: true,
  permissions: [],

  // Single definition of "can manage this clinic's plan/settings" (CLA-906) —
  // Sidebar/ManageLayout previously each recomputed a variant of this.
  canManageClinic: (): boolean => {
    const state: AuthState = useAuthStore.getState()
    return (
      state.isAdmin ||
      state.hasPermission('settings:manage') ||
      state.hasPermission('orgs:manage')
    )
  },

  isPlatformAdmin: (): boolean => useAuthStore.getState().hasPermission('admin:*'),

  isPlatformStaff: (): boolean => {
    const state = useAuthStore.getState()
    if (state.isPlatformAdmin()) return true
    const roles = state.user?.platform_roles ?? []
    return roles.includes('superadmin') || roles.includes('support')
  },

  activeClinicRole: (): string | null => {
    const { clinics, activeClinicId } = useAuthStore.getState()
    return clinics.find((c) => c.id === activeClinicId)?.clinic_role ?? null
  },

  isReadOnly: (): boolean =>
    useAuthStore.getState().activeClinicRole() === 'clinic_viewer',

  hasPermission: (permission: string) => {
    const perms = useAuthStore.getState().permissions
    if (perms.includes('*')) return true
    if (perms.includes(permission)) return true
    // Prefix wildcards: admin:* matches admin:users
    const parts = permission.split(':')
    for (let i = 1; i < parts.length; i++) {
      if (perms.includes(parts.slice(0, i).join(':') + ':*')) return true
    }
    return false
  },

  init: async () => {
    // A fresh call supersedes any scheduled retry (but keeps the backoff
    // counter — only an answer from the server resets it).
    if (initRetryTimer) {
      clearTimeout(initRetryTimer)
      initRetryTimer = null
    }
    try {
      // The settings endpoint returns user context including clinics
      const data = await api.get<{
        user: User & { permissions?: string[] }
        clinics: Clinic[]
        active_clinic_id: number | null
        active_network_id: number | null
        // Optional until every deployed backend returns it (CLA-1183); absent
        // means fall back to the old clinics-length heuristic.
        needs_owner_onboarding?: boolean
        // Optional until the CLA-1018 backend is deployed; absent means an
        // account with nothing to verify.
        email_verification?: EmailVerificationStatus
        demo_mode?: boolean
        telehealth_configured?: boolean
        // Deployment realm ('au' | 'us') — optional until the backend adds it
        // to /auth/me; absent means the pre-realm backend, i.e. AU (CLA-859).
        realm?: string
      }>('/auth/me')
      setRealm(data.realm)
      // CLA-1894: the account's colour theme wins over this browser's boot hint.
      // A backend that predates the field sends nothing: keep what is painted.
      const colorTheme: unknown = data.user.color_theme
      if (typeof colorTheme === 'string') applyColorTheme(colorTheme)
      const perms = data.user.permissions || []
      const isAdmin = perms.includes('*') || perms.some(p => p.startsWith('admin:'))
      set({
        user: data.user,
        clinics: data.clinics,
        activeClinicId: data.active_clinic_id,
        activeNetworkId: data.active_network_id,
        needsOwnerOnboarding: data.needs_owner_onboarding ?? null,
        emailVerification: data.email_verification ?? null,
        isAuthenticated: true,
        isAdmin,
        demoMode: data.demo_mode || false,
        telehealthConfigured: data.telehealth_configured ?? true,
        permissions: perms,
        isLoading: false,
        isRetrying: false,
      })

      initRetryAttempt = 0
      setSentryUser({ id: data.user.id, clinicId: data.active_clinic_id })

      // A DIFFERENT person just signed in on this browser — clear the previous
      // one's residue (CLA-1370 follow-up).
      //
      // `logout()` already does this, so this covers the paths that reach a new
      // session WITHOUT a logout: a session expires and someone else signs in.
      // Both routes out of an expiry deliberately preserve local state so the
      // original clinician doesn't lose unsaved work — which is right for them
      // and wrong the moment it is somebody else.
      //
      // It matters because drafts are keyed by ENTITY, not by user:
      // `clinical-history:<patientUuid>`, `patient-notes:<patientUuid>`. Notes
      // are user-scoped server-side so a stale note draft can never be opened
      // by anyone else, but any clinician who can open patient 17 would be
      // shown the previous user's unsaved clinical history for patient 17 as a
      // recoverable draft — and could save it under their own name.
      handleUserChange(data.user.id)

      // Register for web push notifications (non-blocking)
      registerWebPush().catch(() => {})
    } catch (err) {
      // Only a definitive "you are not authenticated" clears the session
      // (CLA-1015). The client's 401 path has already attempted a silent
      // session refresh by the time the error reaches us.
      const status = err instanceof ApiError ? err.status : undefined
      if (status === 401 || status === 403) {
        initRetryAttempt = 0
        set({ isLoading: false, isRetrying: false, isAuthenticated: false })
        // No session — redirect to login (unless already on a public page),
        // carrying the intended destination so sign-in lands back here
        // instead of on "/" (CLA-1032 — the founder email's "Choose your
        // plan" CTA points at /settings#plan, which this used to drop).
        const path = window.location.pathname
        if (!path.startsWith('/login') && !path.startsWith('/meet/') && !path.startsWith('/forgot-password') && !path.startsWith('/reset-password')) {
          const next = path + (window.location.search || '') + (window.location.hash || '')
          window.location.href =
            next === '/' ? '/login' : '/login?next=' + encodeURIComponent(next)
        }
        return
      }

      // Transient (429 / 5xx / network): the session cookie may be perfectly
      // valid — keep the current auth state and retry in the background,
      // honouring Retry-After when the server sent one (429s do).
      const backoff = Math.min(INIT_RETRY_BASE_MS * 2 ** initRetryAttempt, INIT_RETRY_MAX_MS)
      const delay = err instanceof ApiError && err.retryAfterMs !== undefined
        ? err.retryAfterMs
        : backoff
      initRetryAttempt++
      // Tell the UI this is a wait, not a hang (CLA-1170). Cleared by the next
      // definitive answer — success or 401/403 — whichever arrives.
      set({ isRetrying: true })
      initRetryTimer = setTimeout(() => {
        initRetryTimer = null
        void useAuthStore.getState().init()
      }, delay)
    }
  },

  refreshEmailVerification: async () => {
    try {
      const status = await api.get<EmailVerificationStatus>('/auth/email-verification')
      set({ emailVerification: status })
      return status
    } catch {
      // A failed read keeps whatever we had; the banner shows its own error.
      return null
    }
  },

  switchClinic: async (clinicId: number) => {
    const form = new FormData()
    form.append('clinic_id', String(clinicId))
    await api.postForm('/clinic/switch', form)
    set({ activeClinicId: clinicId })
    const user = useAuthStore.getState().user
    if (user) setSentryUser({ id: user.id, clinicId })
  },

  logout: async () => {
    cancelInitRetry()
    setSentryUser(null)
    // Clear sensitive data from localStorage first (the navigation below
    // tears down the SPA).
    clearSensitiveStorage()
    // CLA-1532: sign out on the SERVER. This POST revokes the refresh token
    // and clears both HttpOnly cookies, which only the server can do. It has
    // to be this path: the `claro_refresh` cookie is scoped to
    // `/auth/session-refresh`, so the browser sends it nowhere else, and a
    // sign-out that never sees the token cannot revoke it. (The old full-page
    // GET to /logout cleared the cookies but left the token live for 30 days.)
    //
    // If the POST fails, fall back to the /logout page, whose button makes
    // the same POST. Never strand the user looking signed in.
    let signedOut = false
    try {
      const res = await fetch(apiUrl('/auth/session-refresh/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      signedOut = res.ok
    } catch {
      signedOut = false
    }
    window.location.href = signedOut ? '/login' : '/logout'
  },
}))

/**
 * Remembers who last held a session in this browser, and wipes the previous
 * user's local residue when that changes (CLA-1370 follow-up).
 *
 * Deliberately NOT in `KEEP_PREFIXES`: `logout()` should forget it. After a
 * logout the storage is already clean, so the next sign-in has nothing to
 * clear and simply records whoever arrives. The stamp only needs to survive
 * the paths that skip `logout()` entirely, which is exactly the expiry case
 * this guards.
 *
 * Stores a user id and nothing else — no PHI.
 */
const LAST_USER_KEY = 'claro:last-user'

function handleUserChange(userId: number | string | undefined): void {
  if (userId === undefined || userId === null) return
  const current = String(userId)
  try {
    const previous = localStorage.getItem(LAST_USER_KEY)
    // No previous stamp means a clean browser or a completed logout — nothing
    // to leak, so don't destroy the drafts of the person who just arrived.
    if (previous && previous !== current) clearSensitiveStorage()
    localStorage.setItem(LAST_USER_KEY, current)
  } catch {
    /* storage unavailable (private mode) — nothing stored means nothing leaked */
  }
}

/**
 * Remove clinical/patient data from localStorage and sessionStorage on logout.
 *
 * Defense-in-depth for shared workstations (SEC-014): selective key removal leaves
 * residues — frecency, drafts, reaction history, etc. — readable by the next user.
 * Strategy: snapshot a small keep-list of non-sensitive preferences, clear everything,
 * then restore the keep-list. Anything not explicitly preserved is treated as sensitive.
 */
function clearSensitiveStorage() {
  // Keys that are safe to preserve across users (UI preferences only — no PHI).
  // claro:realm mirrors the HttpOnly routing cookie, which survives logout too;
  // claro:jurisdiction is the deployment's public vocabulary block (CLA-859).
  //
  // claro:passkey-offer MUST survive (CLA-893) — it records that someone
  // declined the passkey prompt, or flagged this browser as a shared computer.
  // Wiping it on logout would re-ask on the very next sign-in, turning a
  // capped, twice-only offer into a nag that can never be dismissed, and would
  // silently discard the shared-computer warning on exactly the machines where
  // it matters most. Contents are a user id and a counter — no PHI.
  //
  // claro:role-nudge is preserved for exactly the same reason (CLA-1141
  // follow-up): it records that someone pressed "Not now" on the profession
  // banner. Wiping it on logout would re-nag on the next sign-in, which for
  // anyone who signs out at the end of a day is a nag that can never be
  // dismissed. Contents are a user id and an expiry timestamp — no PHI.
  const KEEP_PREFIXES = [
    'claro:theme',
    'claro:language',
    'claro:realm',
    'claro:jurisdiction',
    'claro:passkey-offer',
    'claro:role-nudge',
    // claro:practitioner-types is the served profession vocabulary (CLA-1148),
    // cached so a label renders correctly on the next load before the fetch
    // returns. A platform list, identical for every user — nothing personal in
    // it, and wiping it would only make the first render after a logout fall
    // back to whatever this build shipped with.
    'claro:practitioner-types',
    // claro:billing-interval:<user id> is the Monthly/Annual choice on the plan
    // card and upgrade panel (CLA-1622). Kept so a choice, once made, sticks —
    // keyed per user, so the next person on this browser never inherits it.
    // Contents are a user id and "annual" or "monthly" — no PHI.
    'claro:billing-interval',
  ]

  try {
    const preserved: Array<[string, string]> = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      if (KEEP_PREFIXES.some(p => key === p || key.startsWith(p + ':'))) {
        const value = localStorage.getItem(key)
        if (value !== null) preserved.push([key, value])
      }
    }
    localStorage.clear()
    for (const [key, value] of preserved) {
      localStorage.setItem(key, value)
    }
  } catch { /* ignore storage errors */ }

  // Also clear sessionStorage — frecency lives here now (SEC-026) and may hold PHI
  try { sessionStorage.clear() } catch { /* ignore */ }

  // Clear call recording IndexedDB (contains audio data)
  try { indexedDB.deleteDatabase('claro-call-recordings') } catch { /* ignore */ }
}

async function registerWebPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return

  try {
    const registration = await navigator.serviceWorker.register('/sw.js')

    // Get VAPID public key from server
    const { public_key } = await api.get<{ public_key?: string }>('/messaging/push/vapid-key')
    if (!public_key) return

    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      // Request permission and subscribe
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return

      const applicationServerKey = Uint8Array.from(
        atob(public_key.replace(/-/g, '+').replace(/_/g, '/')),
        c => c.charCodeAt(0)
      )

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })
    }

    // Send subscription to backend
    await api.post('/messaging/push/subscribe', { subscription: subscription.toJSON() })
  } catch (e) {
    console.debug('[push] Registration failed:', e)
  }
}
